import { useMemo } from 'react';

import { useRouter } from 'next/router';

import I18n from '~/src/common/services/I18n';
import { isString, isTruthy } from '~/src/common/utils/guards';

import type { Item } from '../types';
import { getCrumb, getCrumbFromItem, isValidCrumb } from '../utils';

import { useBreadcrumbFromId } from './useBreadcrumbFromId';
import { useBreadcrumbFromItem } from './useBreadcrumbFromItem';
import { useBreadcrumbFromName } from './useBreadcrumbFromName';

const useBreadcrumb = (item: Item | null, categoryIdOrSlug: string | null) => {
  // Récupération des surcharges possibles venant de l'url
  const { query } = useRouter();
  const categoryId = isString(query.categoryId) ? query.categoryId : null;
  const subcategoryId = isString(query.subcategoryId) ? query.subcategoryId : null;
  const categoryName = isString(query.category) ? query.category : null;
  const subcategoryName = isString(query.subcategory) ? query.subcategory : null;

  // Si le produit est fourni, son breadcrumb contient la catégorie finale
  const itemCatId = item?.breadcrumbs?.[item.breadcrumbs.length - 1]?.id ?? null;

  // Les différentes façon de générer le breadcrumb, par ordre de priorité
  const breadcrumbs = [
    useBreadcrumbFromName(categoryName, subcategoryName), // Legacy, via le nom de la catégorie
    useBreadcrumbFromId(categoryIdOrSlug), // Surcharge manuelle par un ID au niveau du composant
    useBreadcrumbFromId(subcategoryId), // Surcharge via un ID de sous-catégorie dans l'URL
    useBreadcrumbFromId(categoryId), // Surcharge via un ID de catégorie dans l'URL
    useBreadcrumbFromId(itemCatId), // Depuis le produit/recette - Amélioré par la navigation
    useBreadcrumbFromItem(item), // Depuis le produit/recette - Version de base du backend
  ];

  // Récupération du path le plus probable, avec une priorité pour les surcharges
  const crumbs = breadcrumbs.find(c => c?.length);

  // Ajout des extrémités (home + produit/recette) et filtrage
  return useMemo(() => {
    const pathCrumb = crumbs ?? [];
    const itemCrumb = item ? getCrumbFromItem(item) : null;
    const homeCrumb = getCrumb('home', { id: 'home', name: I18n.t('common.home') });
    return [homeCrumb, ...pathCrumb, itemCrumb].filter(isTruthy).filter(isValidCrumb);
  }, [crumbs, item]);
};

export default useBreadcrumb;
